import { FC, MouseEvent, useCallback } from 'react';
import { IconBookmark } from '../../../icon/IconBookmark';
import { IconBookmarkFilled } from '../../../icon/IconBookmarkFilled';
import { IconDislike } from '../../../icon/IconDislike';
import { IconDislikeFilled } from '../../../icon/IconDislikeFilled';
import { IconLike } from '../../../icon/IconLike';
import { IconShare } from '../../../icon/IconShare';
import { IconButton } from '../../../input/IconButton';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { PostUI, ReactionState } from '../../types';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import { PostControlsContainer } from '../styles';
import { makeMediaParams } from '../utils';
import { isPostStatusValidForSave } from './utils/isPostStatusValidForSave';

interface PostControlsProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    onReaction: PostDetailsCardProps['onReaction'];
    onBookmark?: PostDetailsCardProps['onBookmark'];
    onShare?: PostDetailsCardProps['onShare'];
}

export const PostControls: FC<PostControlsProps> = ({
    post,
    index,
    onReaction,
    onBookmark,
    onShare,
}) => {
    const { activeIndex, sizes } = usePostDetailsCardContext();

    const handleBookmark = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onBookmark?.();
            event.stopPropagation();
        },
        [onBookmark],
    );

    const handleReaction = useCallback(
        (reactionState: PostUI['reactionState']) => {
            onReaction({ postNumber: index, reactionState });
        },
        [index, onReaction],
    );

    const handleShare = useCallback(
        () => onShare?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [post.content, activeIndex, onShare],
    );

    return (
        <PostControlsContainer
            bottom={1.5}
            left={`calc(${sizes.width}px - 52px)`}
            maxWidth={{ sm: '100%' }}
        >
            <IconButton
                dataTestId={DataTestId.LIKE}
                onClick={(e) => {
                    e.stopPropagation();
                    handleReaction(ReactionState.LIKE);
                }}
            >
                <IconLike
                    fontSize={24}
                    strokeWidth="2px"
                    stroke="#FFFFFF"
                    fill={post.reactionState === 'like' ? '#FFFFFF' : 'transparent'}
                    dataTestId={
                        post.reactionState === 'like' ? DataTestId.FILLED : DataTestId.EMPTY
                    }
                />
            </IconButton>

            {post.business ? (
                <IconButton
                    dataTestId={DataTestId.DISLIKE}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReaction(ReactionState.DISLIKE);
                    }}
                >
                    {post.reactionState === 'dislike' ? (
                        <IconDislikeFilled
                            fontSize={24}
                            strokeWidth="2px"
                            fill="#FFFFFF"
                            dataTestId={DataTestId.FILLED}
                        />
                    ) : (
                        <IconDislike
                            fontSize={24}
                            strokeWidth="2px"
                            fill="#FFFFFF"
                            dataTestId={DataTestId.EMPTY}
                        />
                    )}
                </IconButton>
            ) : null}

            {isPostStatusValidForSave(post.status) && !post.ownersData?.isOwner && (
                <IconButton onClick={handleBookmark} dataTestId={DataTestId.BOOKMARK}>
                    {post.saved ? (
                        <IconBookmarkFilled fontSize={24} color="#FFFFFF" />
                    ) : (
                        <IconBookmark fontSize={24} color="#FFFFFF" />
                    )}
                </IconButton>
            )}

            <IconButton onClick={handleShare} dataTestId={DataTestId.SHARE}>
                <IconShare color="#FFFFFF" />
            </IconButton>
        </PostControlsContainer>
    );
};
